import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChartLine, RefreshCw } from 'lucide-react';
import './EspPage.css';

const API_URL = 'https://api.interdok.qzz.io/esp/d/light';
const REQUEST_URL = process.env.NODE_ENV === 'development' ? '/api/esp/d/light' : API_URL;

const CHART = { width: 960, height: 360, left: 52, right: 24, top: 24, bottom: 40 };

const timeFormat = new Intl.DateTimeFormat('zh-CN', {
  month: 'numeric',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function parseTime(value) {
  const time = new Date(value.replace(/\//g, '-').replace(' ', 'T'));
  return Number.isNaN(time.getTime()) ? null : time;
}

/** 统一记录方向为时间正序，并丢掉无法解析的条目 */
function normalizeRecords(payload) {
  const list = Array.isArray(payload?.records) ? payload.records : [];
  return list
    .map((record) => ({
      id: record.id,
      value: Number(record.value),
      time: parseTime(String(record.updated_at ?? '')),
    }))
    .filter((record) => Number.isFinite(record.value))
    .sort((a, b) => (a.time && b.time ? a.time - b.time : a.id - b.id));
}

function formatValue(value) {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

/** 按 1 / 2 / 5 × 10^n 取整齐的刻度步长 */
function niceStep(rough) {
  if (!Number.isFinite(rough) || rough <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const normalized = rough / magnitude;
  const factor = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return factor * magnitude;
}

function buildScale(records) {
  const values = records.map((record) => record.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || Math.max(Math.abs(max) * 0.1, 1);
  const step = niceStep(span / 4);
  const low = Math.floor(min / step) * step;
  const high = Math.ceil(max / step) * step;
  const ticks = [];
  for (let value = low; value <= high + step / 2; value += step) {
    ticks.push(Number(value.toFixed(10)));
  }
  return { low, high: ticks[ticks.length - 1], ticks };
}

function LineChart({ records }) {
  const [hoverIndex, setHoverIndex] = useState(null);
  const { low, high, ticks } = useMemo(() => buildScale(records), [records]);

  const plotWidth = CHART.width - CHART.left - CHART.right;
  const plotHeight = CHART.height - CHART.top - CHART.bottom;
  const baseline = CHART.top + plotHeight;

  // 单条记录时放在中间，避免除以 0
  const x = (index) =>
    records.length === 1
      ? CHART.left + plotWidth / 2
      : CHART.left + (index / (records.length - 1)) * plotWidth;
  const y = (value) => CHART.top + (1 - (value - low) / (high - low || 1)) * plotHeight;

  const points = records.map((record, index) => `${x(index)},${y(record.value)}`).join(' ');
  const area = `${x(0)},${baseline} ${points} ${x(records.length - 1)},${baseline}`;

  const labelIndexes =
    records.length <= 4
      ? records.map((_, index) => index)
      : [0, Math.floor((records.length - 1) / 2), records.length - 1];

  const onPointerMove = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const position = ((event.clientX - bounds.left) / bounds.width) * CHART.width;
    const ratio = (position - CHART.left) / plotWidth;
    const index = Math.round(ratio * (records.length - 1));
    setHoverIndex(Math.min(records.length - 1, Math.max(0, index)));
  };

  const hovered = hoverIndex === null ? null : records[hoverIndex];

  return (
    <div className="esp-chart">
      <svg
        viewBox="0 0 960 360"
        role="img"
        aria-label={`光照数据折线图，共 ${records.length} 条记录`}
        onPointerMove={onPointerMove}
        onPointerLeave={() => setHoverIndex(null)}
      >
        <defs>
          <linearGradient id="esp-area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--esp-accent)" stopOpacity="0.28" />
            <stop offset="100%" stopColor="var(--esp-accent)" stopOpacity="0" />
          </linearGradient>
        </defs>

        {ticks.map((tick) => (
          <g key={tick}>
            <line
              className="esp-chart__grid"
              x1={CHART.left}
              x2={CHART.width - CHART.right}
              y1={y(tick)}
              y2={y(tick)}
            />
            <text
              className="esp-chart__tick"
              x={CHART.left - 10}
              y={y(tick)}
              textAnchor="end"
              dominantBaseline="middle"
            >
              {formatValue(tick)}
            </text>
          </g>
        ))}

        {labelIndexes.map((index) => (
          <text
            key={records[index].id}
            className="esp-chart__tick"
            x={x(index)}
            y={CHART.height - 14}
            textAnchor={index === 0 ? 'start' : index === records.length - 1 ? 'end' : 'middle'}
          >
            {records[index].time ? timeFormat.format(records[index].time) : `#${records[index].id}`}
          </text>
        ))}

        <polygon className="esp-chart__area" points={area} />
        <polyline className="esp-chart__line" points={points} />

        {records.map((record, index) => (
          <circle
            key={record.id}
            className={`esp-chart__dot${index === hoverIndex ? ' is-active' : ''}`}
            cx={x(index)}
            cy={y(record.value)}
            r={index === hoverIndex ? 5 : 3}
          />
        ))}

        {hovered && (
          <g className="esp-chart__marker" pointerEvents="none">
            <line x1={x(hoverIndex)} x2={x(hoverIndex)} y1={CHART.top} y2={baseline} />
            <circle cx={x(hoverIndex)} cy={y(hovered.value)} r="5.5" />
            <text
              x={Math.min(Math.max(x(hoverIndex), CHART.left + 62), CHART.width - 80)}
              y={Math.max(y(hovered.value) - 16, CHART.top + 14)}
              textAnchor="middle"
            >
              {formatValue(hovered.value)}
            </text>
          </g>
        )}
      </svg>

      {hovered && (
        <p className="esp-chart__readout" aria-live="polite">
          {formatValue(hovered.value)}
          <span>{hovered.time ? timeFormat.format(hovered.time) : `记录 #${hovered.id}`}</span>
        </p>
      )}
    </div>
  );
}

function EspPage() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const requestRef = useRef(null);

  const load = useCallback(async () => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setStatus((current) => (current === 'ready' ? 'refreshing' : 'loading'));
    setError('');

    try {
      const response = await fetch(REQUEST_URL, {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setRecords(normalizeRecords(data));
      setUpdatedAt(new Date());
      setStatus('ready');
    } catch (requestError) {
      if (requestError.name === 'AbortError') return;
      setError('暂时无法获取数据，请稍后重试。');
      setStatus((current) => (current === 'refreshing' ? 'ready' : 'error'));
    }
  }, []);

  useEffect(() => {
    load();
    return () => requestRef.current?.abort();
  }, [load]);

  const stats = useMemo(() => {
    if (!records.length) return null;
    const values = records.map((record) => record.value);
    const latest = records[records.length - 1];
    return {
      latest: latest.value,
      min: Math.min(...values),
      max: Math.max(...values),
      average: values.reduce((sum, value) => sum + value, 0) / values.length,
      latestTime: latest.time,
    };
  }, [records]);

  return (
    <main className="esp-page">
      <header className="esp-page__bar">
        <button
          type="button"
          className="esp-page__back"
          onClick={() => navigate('/desktop')}
          aria-label="返回桌面"
        >
          <ArrowLeft size={18} aria-hidden="true" />
          桌面
        </button>
        <span className="esp-page__name">ESP 前端展示</span>
      </header>

      <section className="esp-page__intro">
        <span className="esp-page__icon">
          <ChartLine size={26} strokeWidth={1.6} aria-hidden="true" />
        </span>
        <div>
          <h1 className="esp-page__title">光照数据</h1>
          <p className="esp-page__desc">最近采集记录的数值变化。</p>
        </div>
        <button
          type="button"
          className="esp-page__refresh"
          onClick={load}
          disabled={status === 'loading' || status === 'refreshing'}
        >
          <RefreshCw
            size={16}
            className={status === 'loading' || status === 'refreshing' ? 'is-spinning' : ''}
            aria-hidden="true"
          />
          刷新
        </button>
      </section>

      <section className="esp-card" aria-busy={status === 'loading' || status === 'refreshing'}>
        {status === 'loading' && <p className="esp-card__state">正在获取数据…</p>}

        {status !== 'loading' && error && (
          <div className="esp-card__state" role="alert">
            <p>{error}</p>
            <button type="button" className="esp-card__retry" onClick={load}>
              重新加载
            </button>
          </div>
        )}

        {status !== 'loading' && !error && records.length === 0 && (
          <p className="esp-card__state">接口暂时没有返回可绘制的数据。</p>
        )}

        {records.length > 0 && !error && <LineChart records={records} />}
      </section>

      {stats && !error && (
        <>
          <dl className="esp-stats">
            <div>
              <dt>最新</dt>
              <dd>{formatValue(stats.latest)}</dd>
            </div>
            <div>
              <dt>平均</dt>
              <dd>{formatValue(Number(stats.average.toFixed(1)))}</dd>
            </div>
            <div>
              <dt>最低</dt>
              <dd>{formatValue(stats.min)}</dd>
            </div>
            <div>
              <dt>最高</dt>
              <dd>{formatValue(stats.max)}</dd>
            </div>
          </dl>
          <p className="esp-page__meta">
            {records.length} 条记录
            {stats.latestTime ? ` · 最新采集 ${timeFormat.format(stats.latestTime)}` : ''}
            {updatedAt ? ` · 页面更新 ${timeFormat.format(updatedAt)}` : ''}
          </p>
        </>
      )}
    </main>
  );
}

export default EspPage;
